import { match, P } from "ts-pattern";
import { isSingleValue } from "../array";
import asArray from "../as-array";
import { appendTimezone, timestampRangeString } from "../date";
import { FilterType } from "../types/filters";
import { filterCollection, flattenOptionGroup, hasSelectedValue, isOptionGroupArray, isValidFilter, mapCollection, scopeFilter, } from "./filter-collection";
/**
 * Decode an encoded SparseFilterCollection (probably from a query string or localStorage) into a
 * full FilterCollection.
 */
export function decodeFilterCollection(config, encodedFilters) {
    if (Object.keys(encodedFilters).length > 0) {
        // Only attempt to decode the SparseFilterCollection if it contains any values. Otherwise
        // the default selected filters from the `config` FilterCollection will be used.
        try {
            const qsFilterConfig = Object.fromEntries(Object.keys(encodedFilters)
                .filter(isValidFilter(config)) // Ensure the query string filter exists in the config
                .filter((id) => asArray(encodedFilters[id]).length) // Ensure a value is selected
                .map((id) => {
                const filterConfig = config[id];
                let selected;
                const valueOrValues = encodedFilters[id];
                if (isSingleValue(valueOrValues)) {
                    // Convert single values to a single FilterOption
                    selected = reconstructSparseOption(filterConfig, valueOrValues);
                }
                else {
                    // Convert an array of values to an array of FilterOption
                    selected = asArray(valueOrValues).map((value) => reconstructSparseOption(filterConfig, value));
                }
                return [id, Object.assign(Object.assign({}, filterConfig), { selected })];
            }));
            // Ensure permanent filters are always present
            Object.values(getPermanentFilters(config)).forEach((filter) => {
                if (!(filter.id in qsFilterConfig) || asArray(qsFilterConfig[filter.id].selected).length === 0) {
                    qsFilterConfig[filter.id] = filter;
                }
            });
            // Apply scoping to ensure the selected filters are valid
            return mapCollection(qsFilterConfig, (filter) => scopeFilter(config, qsFilterConfig, filter));
        }
        catch (err) {
            // Use default value
            console.error("Invalid filters", err, encodedFilters);
        }
    }
    return filterCollection(config, hasSelectedValue);
}
/**
 * Encode a FilterCollection into a string that can be stored as a URL query parameter
 */
export function encodeFilterCollection(config, filters) {
    return Object.fromEntries(Object.values(filters)
        .filter(hasSelectedValue)
        .map((filter) => {
        if (Array.isArray(filter.selected)) {
            const selected = filter.selected;
            return [filter.id, selected.map((option) => getSparseValue(filter, option))];
        }
        return [filter.id, getSparseValue(filter, filter.selected)];
    }));
}
export function getPermanentFilters(filters) {
    return filterCollection(filters, (filter) => filter.removable === false);
}
/**
 * Get a sparse value from an option, returning only the minimum amount of data required to
 * rebuild the full option later.
 */
export function getSparseValue(filter, option) {
    // Important: If you add or change any of these sparse objects, please ensure your changes are
    // mirrored in reconstructSparseOption().
    return match(filter)
        .with({ type: FilterType.DATE_RANGE }, () => {
        const value = option.value;
        if ("secondsAgo" in value) {
            const sparseVal = { r: value.secondsAgo };
            if (value.compare) {
                sparseVal.c = 1;
            }
            return sparseVal;
        }
        else {
            const sparseVal = { s: value.startDate, e: value.endDate };
            if (value.compareStartDate && value.compareEndDate) {
                sparseVal.cs = value.compareStartDate;
                sparseVal.ce = value.compareEndDate;
            }
            return sparseVal;
        }
    })
        .with({ type: FilterType.KEY_VALUE }, () => {
        const sparseVal = {
            k: option.value.key.value,
            v: option.value.values.map((v) => v.value),
        };
        return sparseVal;
    })
        .with({ type: FilterType.THRESHOLD }, () => {
        const sparseVal = {
            m: option.value.metric,
            l: option.value.lowerBound,
            u: option.value.upperBound,
        };
        return sparseVal;
    })
        .otherwise(() => option.value);
}
/**
 * Reconstruct a value that was made sparse by getSparseValue()
 */
export function reconstructSparseOption(filter, value) {
    const enableCompare = filter.enableCompare;
    return match(value)
        .with({ k: P.string, v: P.array(P.string) }, (sparseValue) => {
        const keyFilterOption = findFilterOptionForValue(filter, sparseValue.k);
        return {
            label: "",
            value: {
                key: { label: findLabelForValue(filter, sparseValue.k), value: sparseValue.k },
                values: sparseValue.v.map((value) => {
                    const labelOption = keyFilterOption === null || keyFilterOption === void 0 ? void 0 : keyFilterOption.options.find((option) => option.value === value);
                    const valueLabel = (labelOption === null || labelOption === void 0 ? void 0 : labelOption.label) || value;
                    return { label: valueLabel, value };
                }),
            },
        };
    })
        .with({ k: P.string, v: P.string }, (sparseValue) => {
        const keyFilterOption = findFilterOptionForValue(filter, sparseValue.k);
        const labelOption = keyFilterOption === null || keyFilterOption === void 0 ? void 0 : keyFilterOption.options.find((option) => option.value === sparseValue.v);
        const valueLabel = (labelOption === null || labelOption === void 0 ? void 0 : labelOption.label) || sparseValue.v;
        return {
            label: "",
            value: {
                key: { label: findLabelForValue(filter, sparseValue.k), value: sparseValue.k },
                values: [{ label: valueLabel, value: sparseValue.v }],
            },
        };
    })
        .with({ m: P.string, l: P.optional(P.number), u: P.optional(P.number) }, (sparseValue) => {
        return {
            label: findLabelForValue(filter, sparseValue.m),
            value: {
                metric: sparseValue.m,
                lowerBound: sparseValue.l,
                upperBound: sparseValue.u,
            },
        };
    })
        .with({
        r: P.number,
        c: P.optional(P.number),
    }, (sparseValue) => {
        let label = findLabelForValue(filter, String(sparseValue.r));
        const value = {
            secondsAgo: sparseValue.r,
        };
        if (enableCompare && sparseValue.c) {
            value.compare = true;
            label = `${label} vs Previous Period`;
        }
        return {
            label,
            value,
        };
    })
        .with({
        s: P.number,
        e: P.number,
        cs: P.optional(P.number),
        ce: P.optional(P.number),
    }, (sparseValue) => {
        let label = appendTimezone(timestampRangeString(sparseValue.s, sparseValue.e));
        const value = {
            startDate: sparseValue.s,
            endDate: sparseValue.e,
        };
        if (enableCompare && sparseValue.cs && sparseValue.ce) {
            value.compareStartDate = sparseValue.cs;
            value.compareEndDate = sparseValue.ce;
            label = `${label} vs Previous Period`;
        }
        return {
            label,
            value,
        };
    })
        .otherwise(() => {
        return {
            label: findLabelForValue(filter, value),
            value: value,
        };
    });
}
/**
 * Attempt to find a matching FilterOption for a given value.
 */
function findFilterOptionForValue(config, value) {
    return match(config)
        .with({ type: FilterType.DATE_RANGE, options: P.select() }, (options) => {
        const date = typeof value === "object" ? value : { secondsAgo: value };
        if ("secondsAgo" in date) {
            return findOptionForValue(options, String(date.secondsAgo));
        }
    })
        .with({ type: FilterType.TEXT }, () => {
        return { value, label: value };
    })
        .with({ type: FilterType.LIST, options: P.select() }, (options) => {
        const allOptions = isOptionGroupArray(options) ? flattenOptionGroup(options) : options;
        return findOptionForValue(allOptions, value);
    })
        .with({ type: FilterType.THRESHOLD, options: P.select() }, (options) => {
        const allOptions = flattenOptionGroup(options);
        return findOptionForValue(allOptions, value);
    })
        .with({ options: P.select(P.array({ value: P.any })) }, (options) => {
        // Try the top-level options first
        const topLevelOption = findOptionForValue(options, value);
        if (topLevelOption) {
            return topLevelOption;
        }
        // If no top-level option is found, try the sub-options
        return findOptionForValue(flattenOptionGroup(options), value);
    })
        .otherwise(() => undefined);
}
function findOptionForValue(options, value) {
    return options.find((option) => {
        if (typeof option.value === "object") {
            return option.value === value;
        }
        return String(option.value) === String(value);
    });
}
/**
 * Attempt to find the original label for a given value. If no label can be found, the value itself
 * will be used as the label.
 */
export function findLabelForValue(config, value) {
    const filterOption = findFilterOptionForValue(config, value);
    if (filterOption) {
        return filterOption.label;
    }
    if (typeof value === "string") {
        return value;
    }
    // This should never happen, but it's better to show an empty label rather than stringify an object
    return "";
}
